import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/cellSelections/selectors';
import { getService } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type ADFDocument } from '@canvas/adf';
import { getAdfTextContent } from '@canvas/adf/get-adf-text-content';
import { resolveRichTextValue } from '@canvas/adf/resolve-rich-text-value';
import {
  DatabasesEditor,
  type DatabasesEditorAPI,
} from '@canvas/editor/databases-editor';
import { DatabasesFormattingToolbar } from '@canvas/editor/databases-formatting-toolbar';

import { TEXT_VALUE_MAX_TEXT_AREA_HEIGHT } from './popover-edit-common';
import { type EditComponentProps } from './types';

const POPUP_EXCLUDED_SELECTORS = [
  '[role="menu"]',
  '[role="listbox"]',
  '[role="dialog"]',
  '[data-placement]',
  '[data-ds--level]',
];

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row',
  boxSizing: 'border-box',
  position: 'relative',
});

const lastFieldStylings = xcss({
  flexDirection: 'row-reverse',
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400',
});

const editorWrapperBaseStyles = xcss({
  borderWidth: 'border.width.selected',
  borderStyle: 'solid',
  borderColor: 'color.border.selected',
  borderRadius: 'radius.small',
  backgroundColor: 'elevation.surface',
  overflow: 'auto',
  boxSizing: 'border-box',
  maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
  font: 'font.body',
  width: '100%',
});

const editorTablePadding = xcss({
  paddingTop: 'space.100',
  paddingRight: 'space.075',
  paddingBottom: 'space.100',
});

const editorTablePaddingLeft = {
  paddingLeft: `calc(${token('space.200', '16px')} + ${token('space.025', '2px')})`,
} as const;

const editorSidePanelPadding = xcss({
  paddingBlock: 'space.100',
  paddingInline: 'space.075',
});

const ASK_ROVO_ICON_SIZE = 16;

const floatingToolbarWrapperStyles = xcss({
  position: 'absolute',
  insetInlineStart: 'space.0',
  top: '100%',
  marginBlockStart: 'space.050',
  zIndex: 'blanket',
});

const RichTextValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.TEXT>
> = ({
  field,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  contentId,
  entryId,
  isRenderedInSidePanel,
  ariaLabel,
  'aria-describedby': ariaDescribedBy,
  placeholder,
}) => {
  const onBlurRef = useRef(onBlur);
  const selectionsActions = getService('SelectionsActions');
  onBlurRef.current = onBlur;
  // handleBlur must stay stable (empty deps) so DatabasesEditor does not re-render on every parent render.
  // The ref lets us always invoke the latest onBlur from the stable callback, and on unmount cleanup.
  // We ignore blur events entirely and instead rely on mousedown-outside detection below,
  // because toolbar dropdown portals cause spurious blur events.
  const handleBlur = useCallback((_e: React.FocusEvent) => {
    // no-op — closing is handled by the mousedown listener below
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleOutsideClick = useCallback(() => {
    setHasEditorFocusRef.current?.(false);
    if (containerRef.current?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement)?.blur?.();
    }
    onBlurRef.current?.();
  }, []);

  useHandleOutsideClick(
    handleOutsideClick,
    containerRef,
    POPUP_EXCLUDED_SELECTORS,
    !isDropdownOpen
  );

  useEffect(() => () => onBlurRef.current?.(), []);

  const defaultValue = useMemo(() => resolveRichTextValue(value), [value]);

  const handleChange = useCallback(
    (adf: ADFDocument) => {
      const plain = getAdfTextContent(adf, '');
      onChange({ plain, rich: adf }, { type: DatabaseFieldType.TEXT, adf });
    },
    [onChange]
  );

  const [editorApi, setEditorApi] = useState<DatabasesEditorAPI>(undefined);
  const [isLinkToolbarActive, setIsLinkToolbarActive] = useState(false);

  // Subscribe to the hyperlink plugin shared state to detect when the link
  // floating toolbar is visible, so we can hide the formatting toolbar.
  useEffect(() => {
    if (!editorApi?.hyperlink) {
      return;
    }
    const unsubscribe = editorApi.hyperlink.sharedState.onChange(
      ({ nextSharedState }) => {
        setIsLinkToolbarActive(nextSharedState?.activeLinkMark != null);
      }
    );
    return unsubscribe;
  }, [editorApi]);

  const [hasEditorFocus, setHasEditorFocus] = useState(false);
  const setHasEditorFocusRef = useRef(setHasEditorFocus);
  setHasEditorFocusRef.current = setHasEditorFocus;

  // Track whether the editor has a non-empty text selection so we can show
  // the formatting toolbar only when text is highlighted.
  const [hasTextSelection, setHasTextSelection] = useState(false);
  useEffect(() => {
    const checkSelection = () => {
      const editorEl = containerRef.current?.querySelector('.ProseMirror');
      if (!editorEl) {
        return;
      }
      const pmViewDesc = (editorEl as any).pmViewDesc;
      if (pmViewDesc?.view) {
        const { state } = pmViewDesc.view;
        setHasTextSelection(!state.selection.empty);
        return;
      }
      // Fallback: use the browser's Selection API
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editorEl.contains(sel.anchorNode)) {
        setHasTextSelection(!sel.isCollapsed);
      } else {
        setHasTextSelection(false);
      }
    };

    document.addEventListener('selectionchange', checkSelection);
    return () => document.removeEventListener('selectionchange', checkSelection);
  }, []);

  const isLastField = defaultStoreUtils.useEqualSelector((state) => {
    const fieldIds = selectFieldIdsByContentId(state, { contentId });
    return fieldIds.indexOf(field.id) === fieldIds.length - 1;
  });

  return (
    <Box
      ref={containerRef}
      testId={`rich-text-value-edit-expand-${isLastField ? 'left' : 'right'}`}
      xcss={[
        textAreaContainerStyling,
        isLastField && lastFieldStylings,
        isRenderedInSidePanel && textAreaMinHeightStyling,
      ]}
    >
      <Box
        as="div"
        xcss={[
          editorWrapperBaseStyles,
          isRenderedInSidePanel ? editorSidePanelPadding : editorTablePadding,
        ]}
        style={!isRenderedInSidePanel ? editorTablePaddingLeft : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        data-rich-text-edit-wrapper
      >
        <DatabasesEditor
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={(e) => {
            setHasEditorFocus(true);
            onFocus?.(e as React.FocusEvent<HTMLElement>);
          }}
          onBlur={handleBlur}
          shouldFocus={autoFocus}
          placeholderText={placeholder}
          onEditorApi={setEditorApi}
        />
      </Box>
      {(!isRenderedInSidePanel || hasEditorFocus) &&
        (hasTextSelection || isDropdownOpen) &&
        !isLinkToolbarActive &&
        editorApi && (
          <Box
            xcss={floatingToolbarWrapperStyles}
            onMouseDown={(e: React.MouseEvent) => {
              // Prevent the cell from losing focus (and triggering deselectAll)
              // when interacting with the floating toolbar or its dropdowns.
              e.preventDefault();
            }}
          >
            <DatabasesFormattingToolbar
              editorApi={editorApi}
              onDropdownOpenChange={setIsDropdownOpen}
              askRovoIcon={
                <AskRovoIcon height={ASK_ROVO_ICON_SIZE} width={ASK_ROVO_ICON_SIZE} />
              }
              onAskRovo={() => {
                onBlurRef.current?.();
                if (entryId && !isRenderedInSidePanel) {
                  selectionsActions.selectValues([{ entryId, fieldId: field.id }], {
                    clear: true,
                  });
                }
                openInlineRovoPrompt(InlineRovoPromptTriggerSource.RichTextToolbar);
              }}
            />
          </Box>
        )}
    </Box>
  );
};

export default RichTextValuePopoverEdit;
